import { Component, Input, OnInit, OnDestroy, inject, ChangeDetectionStrategy } from '@angular/core';

import { RouterModule } from '@angular/router';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subject, takeUntil } from 'rxjs';
import { PermissionService, UserRole } from '../../core/services/permission.service';

interface MenuItem {
  id: string;
  label: string;
  route: string;
  svg: string;
  requiredRoles: UserRole[];
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterModule, MatListModule, MatIconModule, MatDividerModule, MatTooltipModule],
  template: `
    <div class="sidebar" id="sidebar" [class.sidebar-on]="isOpen">
    
      <a [routerLink]="'/'" class="sidebar-logo">
        <img src="assets/images/logo.svg" alt="">
        <div class="sidebar-name">
          <strong>Habilitations</strong>
          <span>SIGESR</span>
        </div>
      </a>
    
      <ul class="sidebar-links">
        @for (menu of filteredMenus; track menu) {
          <li [routerLinkActive]="'is-active'">
            <a [routerLink]="menu.route">
              <span [innerHTML]="getSafeSvg(menu.svg)"></span>
              <span>{{ menu.label }}</span>
            </a>
          </li>
        }
      </ul>
    </div>
    
    @if (isOpen) {
      <div class="sidebar-overlay" (click)="isOpen = false"></div>
    }
    `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    .sidebar {
      display: grid;
      grid-template-rows: 30px 1fr 24px;
      gap: 80px;
      background: linear-gradient(117.81deg, #F7FFF6 5.14%, #F8F8F8 42.45%, #FFFAF7 81.78%);
      width: 300px;
      padding: 16px 20px 40px 20px;
      height: 100vh;
      overflow-y: scroll;
      transition: left 0.3s ease-in-out;
    }

    .sidebar-logo {
      color: #263435; text-decoration: none; display: grid;
      grid-template-columns: 52px 1fr; align-items: center; gap: 8px; margin-bottom: 80px;
    }

    .sidebar-logo img { height: 60px; object-fit: contain; }
    .sidebar-name strong, .sidebar-name span { display: block; }
    .sidebar-name strong { font-size: 100%; font-weight: 700; }
    .sidebar-links { padding-left: 0; list-style: none; }
    .sidebar-links li { margin-bottom: 25px; }
    .sidebar-links li a {
      display: flex; justify-content: flex-start; align-items: center;
      gap: 8px; color: #5C6465; text-decoration: none; font-weight: 500; font-size: 15px;
    }
    .sidebar-links li a:hover { color: #27651B; }
    .sidebar-links li.is-active a { color: #27651B; }

    @media screen and (max-width: 1024px) {
      .sidebar { position: absolute; left: -320px; height: calc(100vh - 80px); grid-template-rows: 1fr 60px; top: 80px; }
      .sidebar-logo { display: none; }
      .sidebar.sidebar-on { 
        left: 0;
        bottom: 0;
        width: 50%;
        z-index: 400;
        transition: left 0.3s ease-out; 
      }
    }

    @media screen and (max-width: 480px) {
      .sidebar.sidebar-on {
        width: 75%;
      }
    }

    .sidebar-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0,0,0,0.3);
      z-index: 399;
    }
    
    @media (min-width: 1025px) {
      .sidebar-overlay { display: none; }
    }
  `]
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Input() isOpen = false; 

  private permissions  = inject(PermissionService);
  private sanitizer    = inject(DomSanitizer);
  private destroy$     = new Subject<void>();

  filteredMenus: MenuItem[] = [];

  private menuItems: MenuItem[] = [
    {
      id: 'users',
      label: 'Utilisateurs',
      route: '/dashboard/users',
      requiredRoles: [], // SUPER_ADMIN + IES_ADMIN (accès app suffisant)
      svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20.7739 18C21.5232 18 22.1192 17.5285 22.6543 16.8691C23.7498 15.5194 21.9512 14.4408 21.2652 13.9126C20.5679 13.3756 19.7893 13.0714 18.9999 13M17.9999 11C19.3806 11 20.4999 9.88071 20.4999 8.5C20.4999 7.11929 19.3806 6 17.9999 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M3.22584 18C2.47653 18 1.88055 17.5285 1.34542 16.8691C0.249966 15.5194 2.04855 14.4408 2.73452 13.9126C3.43185 13.3756 4.21046 13.0714 4.99988 13M5.49988 11C4.11917 11 2.99988 9.88071 2.99988 8.5C2.99988 7.11929 4.11917 6 5.49988 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M8.08368 15.1112C7.0619 15.743 4.38286 17.0331 6.01458 18.6474C6.81166 19.436 7.6994 20 8.8155 20H15.1843C16.3004 20 17.1881 19.436 17.9852 18.6474C19.6169 17.0331 16.9379 15.743 15.9161 15.1112C13.52 13.6296 10.4797 13.6296 8.08368 15.1112Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M15.4999 7.5C15.4999 9.433 13.9329 11 11.9999 11C10.0669 11 8.49988 9.433 8.49988 7.5C8.49988 5.567 10.0669 4 11.9999 4C13.9329 4 15.4999 5.567 15.4999 7.5Z" stroke="currentColor" stroke-width="1.5"/>
            </svg>`
    },
    {
      id: 'clients',
      label: 'Applications',
      route: '/dashboard/clients',
      requiredRoles: [UserRole.SCA_SUPER_ADMIN],
      svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 18C2 16.4596 2 15.6893 2.34673 15.1235C2.54074 14.8069 2.80693 14.5407 3.12353 14.3467C3.68934 14 4.45956 14 6 14C7.54044 14 8.31066 14 8.87647 14.3467C9.19307 14.5407 9.45926 14.8069 9.65327 15.1235C10 15.6893 10 16.4596 10 18C10 19.5404 10 20.3107 9.65327 20.8765C9.45926 21.1931 9.19307 21.4593 8.87647 21.6533C8.31066 22 7.54044 22 6 22C4.45956 22 3.68934 22 3.12353 21.6533C2.80693 21.4593 2.54074 21.1931 2.34673 20.8765C2 20.3107 2 19.5404 2 18Z" stroke="currentColor" stroke-width="1.5"/>
              <path d="M14 18C14 16.4596 14 15.6893 14.3467 15.1235C14.5407 14.8069 14.8069 14.5407 15.1235 14.3467C15.6893 14 16.4596 14 18 14C19.5404 14 20.3107 14 20.8765 14.3467C21.1931 14.5407 21.4593 14.8069 21.6533 15.1235C22 15.6893 22 16.4596 22 18C22 19.5404 22 20.3107 21.6533 20.8765C21.4593 21.1931 21.1931 21.4593 20.8765 21.6533C20.3107 22 19.5404 22 18 22C16.4596 22 15.6893 22 15.1235 21.6533C14.8069 21.4593 14.5407 21.1931 14.3467 20.8765C14 20.3107 14 19.5404 14 18Z" stroke="currentColor" stroke-width="1.5"/>
              <path d="M2 6C2 4.45956 2 3.68934 2.34673 3.12353C2.54074 2.80693 2.80693 2.54074 3.12353 2.34673C3.68934 2 4.45956 2 6 2C7.54044 2 8.31066 2 8.87647 2.34673C9.19307 2.54074 9.45926 2.80693 9.65327 3.12353C10 3.68934 10 4.45956 10 6C10 7.54044 10 8.31066 9.65327 8.87647C9.45926 9.19307 9.19307 9.45926 8.87647 9.65327C8.31066 10 7.54044 10 6 10C4.45956 10 3.68934 10 3.12353 9.65327C2.80693 9.45926 2.54074 9.19307 2.34673 8.87647C2 8.31066 2 7.54044 2 6Z" stroke="currentColor" stroke-width="1.5"/>
              <path d="M14 6C14 4.45956 14 3.68934 14.3467 3.12353C14.5407 2.80693 14.8069 2.54074 15.1235 2.34673C15.6893 2 16.4596 2 18 2C19.5404 2 20.3107 2 20.8765 2.34673C21.1931 2.54074 21.4593 2.80693 21.6533 3.12353C22 3.68934 22 4.45956 22 6C22 7.54044 22 8.31066 21.6533 8.87647C21.4593 9.19307 21.1931 9.45926 20.8765 9.65327C20.3107 10 19.5404 10 18 10C16.4596 10 15.6893 10 15.1235 9.65327C14.8069 9.45926 14.5407 9.19307 14.3467 8.87647C14 8.31066 14 7.54044 14 6Z" stroke="currentColor" stroke-width="1.5"/>
            </svg>`
    },
    {
      id: 'groups',
      label: 'Groupes',
      route: '/dashboard/groups',
      requiredRoles: [UserRole.SCA_SUPER_ADMIN],
      svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18.6161 20H19.1063C20.2561 20 21.1707 19.4761 21.9919 18.7436C24.078 16.8826 19.1741 15 17.5 15M15.5 5.06877C15.7271 5.02373 15.9629 5 16.2048 5C18.0247 5 19.5 6.34315 19.5 8C19.5 9.65685 18.0247 11 16.2048 11C15.9629 11 15.7271 10.9763 15.5 10.9312" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M4.48131 16.1112C3.30234 16.743 0.211137 18.0331 2.09388 19.6474C3.01359 20.436 4.03791 21 5.32572 21H12.6743C13.9621 21 14.9864 20.436 15.9061 19.6474C17.7889 18.0331 14.6977 16.743 13.5187 16.1112C10.754 14.6296 7.24599 14.6296 4.48131 16.1112Z" stroke="currentColor" stroke-width="1.5"/>
              <path d="M13 7.5C13 9.70914 11.2091 11.5 9 11.5C6.79086 11.5 5 9.70914 5 7.5C5 5.29086 6.79086 3.5 9 3.5C11.2091 3.5 13 5.29086 13 7.5Z" stroke="currentColor" stroke-width="1.5"/>
            </svg>`
    },
    {
      id: 'roles',
      label: 'Rôles',
      route: '/dashboard/roles',
      requiredRoles: [UserRole.SCA_SUPER_ADMIN],
      svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11.5 14.0116C9.45338 13.9164 7.38334 14.4064 5.57757 15.4816C4.1628 16.324 0.453365 18.0441 2.71266 20.1966C3.81631 21.248 5.04549 22 6.59087 22H12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M15.5 6.5C15.5 8.98528 13.4853 11 11 11C8.51472 11 6.5 8.98528 6.5 6.5C6.5 4.01472 8.51472 2 11 2C13.4853 2 15.5 4.01472 15.5 6.5Z" stroke="currentColor" stroke-width="1.5"/>
              <path d="M18 20.7143V22M18 20.7143C16.8432 20.7143 15.8241 20.1461 15.2263 19.2833M18 20.7143C19.1568 20.7143 20.1759 20.1461 20.7737 19.2833M18 14.2857C19.1569 14.2857 20.1761 14.854 20.7738 15.7169M18 14.2857C16.8431 14.2857 15.8239 14.854 15.2262 15.7169M18 14.2857V13M22 14.9286L20.7738 15.7169M14.0004 20.0714L15.2263 19.2833M14 14.9286L15.2262 15.7169M21.9996 20.0714L20.7737 19.2833M20.7738 15.7169C21.1273 16.2271 21.3333 16.8403 21.3333 17.5C21.3333 18.1597 21.1272 18.773 20.7737 19.2833M15.2262 15.7169C14.8727 16.2271 14.6667 16.8403 14.6667 17.5C14.6667 18.1597 14.8728 18.773 15.2263 19.2833" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>`
    },
    {
      id: 'admin-audit',
      label: 'Audit',
      route: '/dashboard/admin-audit',
      requiredRoles: [], // SUPER_ADMIN + IES_ADMIN
      svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 2H9C7.34315 2 6 3.34315 6 5V19C6 20.6569 7.34315 22 9 22H15C16.6569 22 18 20.6569 18 19V5C18 3.34315 16.6569 2 15 2Z" stroke="currentColor" stroke-width="1.5"/>
              <path d="M9 7H15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M9 11H15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M9 15H12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>`
    },
    {
      id: 'journalisation',
      label: 'Journalisation',
      route: '/dashboard/journalisation',
      requiredRoles: [UserRole.SCA_SUPER_ADMIN],
      svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 5L21 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M3 5L5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M9 12L21 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M3 12L5 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M9 19L21 19" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M3 19L5 19" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>`
    },
  ];

  ngOnInit(): void {
    // Réagit à chaque changement de contexte (ex: refresh token)
    this.permissions.context.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => this.filterMenus());

    // Premier rendu immédiat
    this.filterMenus();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private filterMenus(): void {
    if (!this.permissions.snapshot.canAccessApp) {
      this.filteredMenus = [];
      return;
    }

    this.filteredMenus = this.menuItems.filter(menu => {
      // Pas de restriction → visible par tous ceux qui ont accès à l'app
      if (!menu.requiredRoles.length) return true;
      // Sinon vérifier que l'utilisateur a AU MOINS UN des rôles requis
      return this.permissions.hasAnyRole(...menu.requiredRoles);
    });
  }

  getSafeSvg(svg: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(svg);
  }
}